import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Search, X, Loader2, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import NomineeCard from './NomineeCard';
import PaymentModal from './PaymentModal';
import VotingHeader from './VotingHeader';
import { getRootSurvey } from '../api/voting';

const pad2 = (n) => String(n ?? '').padStart(2, '0');

function buildVotingCode(survey, category, nominee) {
  return pad2(category.code) + pad2(nominee.code);
}

function flatten(survey, categories) {
  const list = [];
  (categories || []).forEach((c) => {
    const cat = c.category;
    (c.nominees || []).forEach((n) => {
      list.push({
        id:           n.id,

        name:         n.nominee,
        photo:        n.photo || null,
        votingCode:   buildVotingCode(survey, cat, n),
        categoryId:   cat.id,
        categoryName: cat.categoryname,
      });
    });
  });
  return list;
}

export default function VotingPortal({ survey: surveyProp, settings }) {
  const [survey, setSurvey]         = useState(surveyProp || null);
  const [nominees, setNominees]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState('');
  const [activeCat, setActiveCat]   = useState(null);
  const [voteTarget, setVoteTarget] = useState(null);

  useEffect(() => {
    if (!settings) return;
    const root = document.documentElement;
    if (settings.primary_color)    root.style.setProperty('--color-primary', settings.primary_color);
    if (settings.secondary_color)  root.style.setProperty('--color-secondary', settings.secondary_color);
    if (settings.background_color) root.style.setProperty('--color-background', settings.background_color);
    if (settings.text_color)       root.style.setProperty('--color-text', settings.text_color);
  }, [settings]);

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('code');
    if (code) setSearch(code);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getRootSurvey();
        if (cancelled) return;
        const s    = res?.data?.survey || surveyProp;
        const cats = res?.data?.categories || [];
        setSurvey(s);
        setCategories(cats.map((c) => c.category));
        setNominees(flatten(s, cats));
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load nominees.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);

  const chipScrollRef    = useRef(null);
  const [canScrollLeft,  setCanScrollLeft]  = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkChipScroll = useCallback(() => {
    const el = chipScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = chipScrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(checkChipScroll);
    observer.observe(el);
    return () => observer.disconnect();
  }, [categories, checkChipScroll]);

  const scrollChips = (dir) => {
    const el = chipScrollRef.current;
    if (el) el.scrollBy({ left: dir * 160, behavior: 'smooth' });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return nominees.filter((n) => {
      if (activeCat && n.categoryId !== activeCat) return false;
      if (!q) return true;
      return (
        n.name.toLowerCase().includes(q) ||
        n.categoryName.toLowerCase().includes(q) ||
        n.votingCode.toLowerCase().includes(q)
      );
    });
  }, [nominees, search, activeCat]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goToPage = useCallback((p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, activeCat]);

  return (
    <div className="voting-portal">
      <div className="container-xl px-3 px-md-4 pt-3 pb-3">

        <VotingHeader survey={survey} settings={settings} showScrollCue />

        {/* Row 2 — Search bar + category chips */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="position-relative mb-3">
              <Search size={16} className="position-absolute text-muted search-icon" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, category, or voting code…"
                className="form-control form-control-lg rounded-pill ps-5 pe-5 shadow-sm"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="btn btn-sm btn-light rounded-circle position-absolute search-clear-btn"
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {categories.length > 0 && (
              <div className="chip-scroll-wrapper">
                {canScrollLeft && (
                  <button className="chip-arrow chip-arrow--left" onClick={() => scrollChips(-1)} aria-label="Scroll left">
                    <ChevronLeft size={13} />
                  </button>
                )}
                <div
                  ref={chipScrollRef}
                  className={`chip-row ${categories.length > 12 ? 'chip-row--grid' : ''}`}
                  onScroll={checkChipScroll}
                >
                  {categories.map((c) => {
                    const active = activeCat === c.id;
                    return (
                      <button
                        key={c.id}
                        onClick={() => setActiveCat(active ? null : c.id)}
                        className={`btn btn-sm rounded-pill text-uppercase fw-semibold chip-btn ${active ? 'gold-chip border-0' : 'btn-outline-secondary'}`}
                      >
                        {c.categoryname}
                      </button>
                    );
                  })}
                  {activeCat && (
                    <button onClick={() => setActiveCat(null)} className="btn btn-sm btn-light rounded-pill chip-btn">
                      Clear
                    </button>
                  )}
                </div>
                {canScrollRight && (
                  <button className="chip-arrow chip-arrow--right" onClick={() => scrollChips(1)} aria-label="Scroll right">
                    <ChevronRight size={13} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Row 3 — Nominee cards */}
        <div className="row">
          <div className="col-12">
            {loading && (
              <div className="d-flex flex-column align-items-center text-primary py-5 gap-2">
                <Loader2 size={32} className="text-warning spin" />
                <p className="fw-medium mb-0 text-primary">Loading nominees…</p>
              </div>
            )}

            {error && !loading && (
              <div className="mx-auto text-center p-4 rounded-3 border border-danger-subtle bg-danger-subtle" style={{ maxWidth: 420 }}>
                <AlertTriangle size={28} className="text-danger mb-2" />
                <p className="fw-semibold mb-1">Could not load nominees</p>
                <p className="small text-primary mb-0">{error}</p>
              </div>
            )}

            {!loading && !error && filtered.length === 0 && (
              <div className="text-center text-primary py-5">
                <p className="font-display fs-4 mb-1">No nominees match your search.</p>
                <p className="small mb-0">Try a different name or clear filters.</p>
              </div>
            )}

            {!loading && !error && filtered.length > 0 && (
              <>
                <p className="nominee-count small mb-3">
                  <span className="fw-semibold">{filtered.length}</span>{' '}
                  {filtered.length === 1 ? 'nominee' : 'nominees'}
                  {totalPages > 1 && (
                    <span className="ms-2">— page {page} of {totalPages}</span>
                  )}
                </p>
                <div className="row g-3 g-md-4">
                  {paginated.map((n) => (
                    <div key={n.id} className="col-12 col-sm-6 col-md-4 col-lg-3 nominee-col">
                      <NomineeCard
                        nominee={n}
                        survey={survey}
                        onVote={(nominee, votes) => setVoteTarget({ nominee, votes })}
                      />
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <nav className="d-flex justify-content-center mt-4" aria-label="Nominees pagination">
                    <ul className="pagination pagination-sm mb-0 flex-wrap justify-content-center gap-1">
                      <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                        <button className="page-link rounded-pill px-3" onClick={() => goToPage(page - 1)} disabled={page === 1}>
                          ‹ Prev
                        </button>
                      </li>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                        <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                          <button className="page-link rounded-pill" onClick={() => goToPage(p)}>{p}</button>
                        </li>
                      ))}
                      <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link rounded-pill px-3" onClick={() => goToPage(page + 1)} disabled={page === totalPages}>
                          Next ›
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </>
            )}
          </div>
        </div>

      </div>

      {voteTarget && survey && (
        <PaymentModal
          open={!!voteTarget}
          onClose={() => setVoteTarget(null)}
          nominee={voteTarget.nominee}
          votes={voteTarget.votes}
          survey={survey}
        />
      )}
    </div>
  );
}
